/**
 * cdn-domain-bot-analytics-tab.tsx — Bot Management Analytics tab for a CDN domain (act-as).
 *
 * Read-only: total traffic vs bot traffic time-series chart, blocked bots stat,
 * top bot types table, top blocked agents table.
 * Uses @vnetwork/ui/charts Chart component (matches codebase pattern).
 *
 * Endpoints: GET /api/v3/cdn/domains/:id/bot-management-analytics?from=&to=
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart2, RefreshCw } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import {
  Card, CardHeader, CardTitle, CardDescription, CardBody,
  Button, Skeleton,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Chart, CHART_COLORS } from '@vnetwork/ui/charts';
import { TabError } from '@/pages/customers/detail/tab-states';
import {
  useActAsCDNBotAnalytics,
  type AnalyticsDataPoint,
  type AnalyticsTopEvent,
} from '@/api/act-as-cdn-security';

/* ── Date helpers ────────────────────────────────────────────────────────── */

function isoOffset(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

const RANGES = [
  { value: '1',  label: 'Last 24 h' },
  { value: '7',  label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
] as const;

/* ── Palette (Muted Material 400) ────────────────────────────────────────── */
const COLOR_TRAFFIC = CHART_COLORS[0]; // blue — total traffic
const COLOR_BOTS    = CHART_COLORS[4]; // red — bot traffic

/* ── Stat card ───────────────────────────────────────────────────────────── */
function StatCard({ label, value, tone }: { label: string; value: number | undefined; tone: 'info' | 'danger' | 'warning' }) {
  return (
    <Card>
      <CardBody style={{ textAlign: 'center', padding: 'var(--vn-space-4)' }}>
        <div style={{ fontSize: 'var(--vn-font-size-2xl)', fontWeight: 700, color: `var(--vn-${tone}-text)` }}>
          {value?.toLocaleString() ?? '—'}
        </div>
        <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', marginTop: 4 }}>{label}</div>
      </CardBody>
    </Card>
  );
}

/* ── Top table ───────────────────────────────────────────────────────────── */
const TOP_EVENT_COLUMNS = (labelHeader: string): ColumnDef<AnalyticsTopEvent, unknown>[] => [
  { id: 'label', accessorKey: 'label', header: labelHeader, cell: ({ row }) => <span style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>{row.original.label}</span> },
  { id: 'count', accessorKey: 'count', header: 'Count', cell: ({ row }) => <span style={{ display: 'block', textAlign: 'right' }}>{row.original.count.toLocaleString()}</span> },
  { id: 'pct', accessorKey: 'pct', header: '%', cell: ({ row }) => <span style={{ display: 'block', textAlign: 'right', color: 'var(--vn-fg-muted)' }}>{row.original.pct != null ? `${row.original.pct.toFixed(1)}%` : '—'}</span> },
];

function TopTable({ title, rows, labelHeader }: { title: string; rows: AnalyticsTopEvent[]; labelHeader: string }) {
  if (!rows.length) return null;
  return (
    <Card>
      <CardHeader><CardTitle>{title}</CardTitle></CardHeader>
      <CardBody style={{ padding: 0 }}>
        <DataTable columns={TOP_EVENT_COLUMNS(labelHeader)} data={rows} dense />
      </CardBody>
    </Card>
  );
}

/* ── Chart data merge ────────────────────────────────────────────────────── */
function mergeTimeSeries(
  traffic: AnalyticsDataPoint[] = [],
  bots: AnalyticsDataPoint[] = [],
): { ts: string; traffic: number; bots: number }[] {
  const map = new Map<string, { traffic: number; bots: number }>();
  for (const p of traffic) map.set(p.ts, { traffic: p.value, bots: 0 });
  for (const p of bots) {
    const row = map.get(p.ts) ?? { traffic: 0, bots: 0 };
    map.set(p.ts, { ...row, bots: p.value });
  }
  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([ts, v]) => ({ ts, ...v }));
}

/* ── Component ───────────────────────────────────────────────────────────── */

interface Props { domainId: string }

export function CdnDomainBotAnalyticsTab({ domainId }: Props) {
  const { t } = useTranslation();
  const [days, setDays] = useState<string>('7');

  const from = isoOffset(Number(days));
  const to   = isoOffset(0);

  const q = useActAsCDNBotAnalytics(domainId, from, to);

  if (q.isLoading) return <Skeleton style={{ height: 380 }} />;
  if (q.isError) {
    return (
      <TabError
        title={t('cdn_domain.bot_analytics_tab.load_error', 'Failed to load bot analytics')}
        onRetry={() => q.refetch()}
      />
    );
  }

  const data      = q.data;
  const chartData = mergeTimeSeries(data?.traffic_series, data?.bot_series);

  return (
    <Section
      title={t('cdn_domain.bot_analytics_tab.title', 'Bot Mgmt Analytics')}
      description={t('cdn_domain.bot_analytics_tab.description', 'Bot traffic breakdown over time. Read-only.')}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>

        {/* Range selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', justifyContent: 'flex-end' }}>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger style={{ width: 140 }}><SelectValue /></SelectTrigger>
            <SelectContent>
              {RANGES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" startIcon={<RefreshCw size={14} />} onClick={() => q.refetch()}>
            {t('cdn_domain.bot_analytics_tab.refresh', 'Refresh')}
          </Button>
        </div>

        {/* Summary stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--vn-space-3)' }}>
          <StatCard label={t('cdn_domain.bot_analytics_tab.stat_traffic', 'Total traffic')} value={data?.total_traffic} tone="info" />
          <StatCard label={t('cdn_domain.bot_analytics_tab.stat_bot_traffic', 'Bot traffic')} value={data?.bot_traffic} tone="warning" />
          <StatCard label={t('cdn_domain.bot_analytics_tab.stat_blocked', 'Blocked bots')} value={data?.blocked_bots} tone="danger" />
        </div>

        {/* Time-series chart */}
        <Card>
          <CardHeader>
            <CardTitle>{t('cdn_domain.bot_analytics_tab.chart_title', 'Traffic vs Bot Traffic')}</CardTitle>
            <CardDescription>
              {t('cdn_domain.bot_analytics_tab.chart_desc', 'Total requests and detected bot requests over the selected period.')}
            </CardDescription>
          </CardHeader>
          <CardBody>
            {chartData.length > 0 ? (
              <Chart
                type="area"
                height={240}
                data={chartData}
                xKey="ts"
                series={[
                  { key: 'traffic', label: 'Traffic', color: COLOR_TRAFFIC },
                  { key: 'bots',    label: 'Bots',    color: COLOR_BOTS },
                ]}
                showLegend
                showGrid
                valueFormatter={(v) => (typeof v === 'number' ? v.toLocaleString() : String(v))}
              />
            ) : (
              <EmptyState
                size="sm"
                icon={<BarChart2 size={20} />}
                title={t('cdn_domain.bot_analytics_tab.no_data', 'No data for this period')}
              />
            )}
          </CardBody>
        </Card>

        {/* Top tables */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'var(--vn-space-4)' }}>
          <TopTable
            title={t('cdn_domain.bot_analytics_tab.top_bot_types', 'Top Bot Types')}
            rows={data?.top_bot_types ?? []}
            labelHeader={t('cdn_domain.bot_analytics_tab.col_type', 'Bot type')}
          />
          <TopTable
            title={t('cdn_domain.bot_analytics_tab.top_blocked_agents', 'Top Blocked User-Agents')}
            rows={data?.top_blocked_agents ?? []}
            labelHeader="User-Agent"
          />
        </div>

      </div>
    </Section>
  );
}
